/**
 * THE THREE READS STATISTICS ADDS (design 18), each keyed on what it asks, so a screen can tell an
 * answer to the question on screen from the last one `useAsync` keeps while it reads the next.
 *
 *   useAllArticleStats  every row of `listArticleStats` over a range, pages of 100 (a budget of 20)
 *                       - the Overview's ranked list ranks them, the Articles tab sorts them. Ranking
 *                       and sorting ORDER the rows; nothing here or there adds two rows together
 *                       (a per-article sum is not the author's figure - ../ArticleStatsRoute.tsx,
 *                       point 3).
 *   useArticleCovers    each article's cover address, from the cabinet's own articles list
 *                       (`listOwnArticles` carries `coverUrl`; the owner, 2026-10-05: covers on the
 *                       Articles tab) - no field was added to the statistics read for it.
 *   useDays             `getDashboardDays`: every day of a range, the author's or one article's.
 *                       ★ A 404 IS "THIS SERVER DOES NOT SERVE THE READ YET" and answers `null`, so
 *                       the screen draws no chart rather than an error (Earlier months' rule,
 *                       routes/payments/PaymentsRoute.tsx `isNotServed`): a cabinet newer than its
 *                       server must not break the page it improves.
 */
import { useCallback } from 'react';
import { NotFoundFailure, isApiProblem, useCabinetApi } from '../../../features/cabinet/api';
import type {
  ArticleListPage,
  ArticleStats,
  ArticleStatsPage,
  DashboardDays,
} from '../../../features/cabinet/api';
import { useAsync } from '../../../features/cabinet/state';
import type { AsyncResult } from '../../../features/cabinet/state';
import type { DayRange } from '../../../features/cabinet/ranges';

/** The largest page the contract allows (`shared.yaml`'s `Limit`). */
const PAGE_LIMIT = 100;

/** A stop, not a claim: 2,000 articles is more than any author here has. */
const PAGE_BUDGET = 20;

/* ------------------------------------------------------------------ every per-article row */

/** Which table the tab shows: the articles, or the videos that earn as they do. */
export type StatsKind = 'article' | 'video';

export interface AllArticleStats {
  /** What was asked: the range, or `undefined` for the server's usual period. */
  readonly asked: DayRange | undefined;
  /** The days the answer covers - the envelope's own, when it carried them. */
  readonly from: string | undefined;
  readonly to: string | undefined;
  readonly items: readonly ArticleStats[];
  /** False when the budget ran out before the last page. */
  readonly complete: boolean;
}

export function useAllArticleStats(
  range: DayRange | undefined,
  kind: StatsKind = 'article',
): AsyncResult<AllArticleStats> {
  const api = useCabinetApi();
  const from = range?.from;
  const to = range?.to;
  const read = useCallback(
    async (signal: AbortSignal): Promise<AllArticleStats> => {
      const items: ArticleStats[] = [];
      let cursor: string | undefined;
      let first: ArticleStatsPage | undefined;
      for (let page = 0; page < PAGE_BUDGET; page += 1) {
        // The page as the table types it: the generated result composes the shared envelope, so
        // its items are narrowed by the named page type (../ArticleStatsRoute.tsx does the same).
        const answer = (await api.listArticleStats(
          {
            limit: PAGE_LIMIT,
            kind,
            ...(from === undefined || to === undefined ? {} : { from, to }),
            ...(cursor === undefined ? {} : { cursor }),
          },
          signal,
        )) as ArticleStatsPage;
        first ??= answer;
        items.push(...answer.items);
        cursor = answer.next_cursor ?? undefined;
        if (cursor === undefined) {
          return {
            asked: from === undefined || to === undefined ? undefined : { from, to },
            from: first.from,
            to: first.to,
            items,
            complete: true,
          };
        }
      }
      return {
        asked: from === undefined || to === undefined ? undefined : { from, to },
        from: first?.from,
        to: first?.to,
        items,
        complete: false,
      };
    },
    [api, from, to, kind],
  );
  return useAsync(read, `statistics:all-${kind}s:${from ?? 'usual'}:${to ?? ''}`);
}

/** Whether an answer is the one to the range on screen, not the last one kept while reading. */
export function answers(stats: AllArticleStats | undefined, range: DayRange | undefined): boolean {
  if (stats === undefined) {
    return false;
  }
  if (range === undefined || stats.asked === undefined) {
    return range === undefined && stats.asked === undefined;
  }
  return stats.asked.from === range.from && stats.asked.to === range.to;
}

/* ------------------------------------------------------------------------------- covers */

export function useArticleCovers(
  kind: StatsKind = 'article',
): AsyncResult<ReadonlyMap<string, string>> {
  const api = useCabinetApi();
  const read = useCallback(
    async (signal: AbortSignal): Promise<ReadonlyMap<string, string>> => {
      const covers = new Map<string, string>();
      let cursor: string | undefined;
      for (let page = 0; page < PAGE_BUDGET; page += 1) {
        const answer = (await api.listOwnArticles(
          { limit: PAGE_LIMIT, kind, ...(cursor === undefined ? {} : { cursor }) },
          signal,
        )) as ArticleListPage;
        for (const item of answer.items) {
          if (typeof item.coverUrl === 'string' && item.coverUrl !== '') {
            covers.set(item.articleId, item.coverUrl);
          }
        }
        cursor = answer.next_cursor ?? undefined;
        if (cursor === undefined) {
          break;
        }
      }
      return covers;
    },
    [api, kind],
  );
  return useAsync(read, `statistics:covers:${kind}`);
}

/* --------------------------------------------------------------------------------- days */

export interface DaysAnswer {
  /** What was asked, so a screen can tell this answer from the last one kept. */
  readonly key: string;
  /** `null`: the server does not serve the read yet (see the header). */
  readonly days: DashboardDays | null;
}

export function daysKey(range: DayRange, articleId?: string): string {
  return `${range.from}..${range.to}:${articleId ?? 'author'}`;
}

export function useDays(range: DayRange, articleId?: string): AsyncResult<DaysAnswer> {
  const api = useCabinetApi();
  const key = daysKey(range, articleId);
  const { from, to } = range;
  const read = useCallback(
    async (signal: AbortSignal): Promise<DaysAnswer> => {
      try {
        const days = await api.getDashboardDays(
          { from, to, ...(articleId === undefined ? {} : { articleId }) },
          signal,
        );
        return { key, days };
      } catch (error) {
        if (error instanceof NotFoundFailure || (isApiProblem(error) && error.status === 404)) {
          return { key, days: null };
        }
        throw error;
      }
    },
    [api, key, from, to, articleId],
  );
  return useAsync(read, `statistics:days:${key}`);
}
